// Sign-in, sign-out, the signed-in user and password rules (D48); administration is in
// admin.ts, permissions in access.ts. The session is an HttpOnly cookie the API sets; this
// module never sees it.
import { queryOptions } from "@tanstack/react-query";

import { api, type User } from "../api/client";
import { ApiError, unwrap } from "../api/queries";
import { messages } from "../i18n";

export const meQuery = () =>
  queryOptions({
    queryKey: ["me"],
    queryFn: () => unwrap(api.GET("/api/auth/me")),
    retry: false,
    staleTime: 60_000,
  });

export function signIn(email: string, password: string): Promise<User> {
  return unwrap(api.POST("/api/auth/login", { body: { email, password } }));
}

/** Ends the session; the API clears the cookie. */
export async function signOut(): Promise<void> {
  const { response } = await api.POST("/api/auth/logout");
  if (!response.ok && response.status !== 401) throw new ApiError(response.status, undefined);
}

/** Every session of the user ends; the response carries a fresh cookie for this browser. */
export function changePassword(currentPassword: string, newPassword: string): Promise<User> {
  return unwrap(
    api.POST("/api/auth/password", { body: { current_password: currentPassword, new_password: newPassword } }),
  );
}

/** The minimum the API enforces for a password someone chooses. */
export const MIN_PASSWORD_CHARS = 10;

/** Why a new password would be refused, or undefined when it is fine. */
export function passwordProblem(password: string, repeat?: string): string | undefined {
  const t = messages().auth.password;
  if ([...password].length < MIN_PASSWORD_CHARS) return t.tooShort(MIN_PASSWORD_CHARS);
  if (repeat !== undefined && password !== repeat) return t.differ;
  return undefined;
}

/** 0–4: long enough, long, mixed case, a digit or symbol. Advice only; the API checks length. */
export function strength(p: string): number {
  if ([...p].length < MIN_PASSWORD_CHARS) return 0;
  let s = 1;
  if ([...p].length >= 16) s++;
  if (/[A-Z]/.test(p) && /[a-z]/.test(p)) s++;
  if (/[^A-Za-z]/.test(p)) s++;
  return s;
}

/**
 * A full page load: after signing in or out the device provider (above the router) must
 * start again with the new session, and nothing of the previous user may stay in memory.
 * An object so tests can replace it.
 */
export const browser = {
  reloadTo(path: string): void {
    window.location.assign(path);
  },
};

/** Sign out and land on the sign-in page with nothing of this session left in memory. */
export async function signOutAndLeave(): Promise<void> {
  try {
    await signOut();
  } finally {
    browser.reloadTo("/sign-in");
  }
}

/** Where to go after signing in: a same-site path from `?next=`, else the console. */
export function nextPath(search: string): string {
  const next = new URLSearchParams(search).get("next");
  return next && next.startsWith("/") && !next.startsWith("//") && next !== "/sign-in" ? next : "/";
}
